import { useState } from 'react'
import type { WorkflowRead } from '../../client'
import Modal from '../ui/Modal'

const INPUT =
  'w-full rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-base text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none sm:text-sm'

type Props = {
  workflow: WorkflowRead
  disabled: boolean
  onRun: (inputs: Record<string, string>) => void
  onClose: () => void
}

export default function RunWorkflowDialog({ workflow, disabled, onRun, onClose }: Props) {
  const [values, setValues] = useState<Record<string, string>>({})
  const ready = workflow.inputs.every(({ name }) => values[name]?.trim())

  return (
    <Modal
      title={`Run ${workflow.name}`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg px-3 text-sm text-stone-600 transition hover:bg-stone-100 sm:h-8"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="run-workflow"
            disabled={!ready || disabled}
            className="h-10 rounded-lg bg-purple-700 px-3 text-sm font-medium text-white transition enabled:hover:bg-purple-800 disabled:opacity-50 sm:h-8"
          >
            Run
          </button>
        </div>
      }
    >
      <form
        id="run-workflow"
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready && !disabled) onRun(values)
        }}
      >
        {workflow.inputs.map((input, index) => (
          <div key={input.name} className="space-y-1">
            <label htmlFor={`run-${input.name}`} className="text-xs font-medium text-stone-700">
              {input.label}
            </label>
            <input
              id={`run-${input.name}`}
              autoFocus={index === 0}
              maxLength={500}
              value={values[input.name] ?? ''}
              onChange={(event) => setValues((current) => ({ ...current, [input.name]: event.target.value }))}
              className={INPUT}
            />
          </div>
        ))}
        <ol className="space-y-1.5 rounded-lg bg-stone-50 p-3">
          {workflow.steps.map((step) => (
            <li key={step.position} className="flex gap-2 text-xs text-stone-600">
              <span className="w-4 shrink-0 text-center font-semibold text-stone-400">{step.position}</span>
              {step.title}
            </li>
          ))}
        </ol>
        <p className="text-xs text-stone-500">
          Opens a new chat and runs these steps. Steps that create or spend still ask first.
        </p>
      </form>
    </Modal>
  )
}
